import { Box, Button, FormHelperText, OutlinedInput, Popover, Stack } from "@mui/material";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { useTranslation } from "react-i18next";
import { usePopover } from "../../../../utils/hooks/use-popover";
import type { BrandKitColor } from "../../../brand-kit/server/brand-kit-service";
import { BrandColours } from "./brand-colours";
import { EyeDropper } from "./eye-dropper";

const HEX_FULL_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const TRANSPARENT_RE = /^(t(r(a(n(s(p(a(r(e(n(t)?)?)?)?)?)?)?)?)?)?)?$/i;

export const ColourPickerV1: FC<{
  popover: ReturnType<typeof usePopover<HTMLElement>>;
  onInput?: (color: string, skipAddingToHistoryStack?: boolean) => void;
  onChange: (color: string, skipAddingToHistoryStack?: boolean) => void;
  colour: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}> = ({ popover, onInput, onChange, colour, inputRef }) => {
  const initialColour = useRef(colour);
  const { t } = useTranslation();

  const [editingValue, setEditingValue] = useState(
    colour === "transparent" ? "transparent" : colour.replace(/^#/, ""),
  );

  const [error, setError] = useState<string | null>(null);

  // Keep local editing text in sync if `colour` changes externally
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEditingValue(colour === "transparent" ? "transparent" : colour.replace(/^#/, ""));
  }, [colour]);

  const handleClose = useCallback(() => {
    // Reset input to original colour if cancelled
    onInput?.(initialColour.current);
    popover.handleClose();
  }, [onInput, popover]);

  const handleConfirm = useCallback(() => {
    const trimmed = editingValue.trim().toLowerCase();
    const candidate = trimmed === "transparent" ? "transparent" : `#${editingValue.trim()}`;

    if (trimmed === "transparent" || HEX_FULL_RE.test(candidate)) {
      onChange(candidate);
      popover.handleClose();
      setError(null);
    } else {
      setError(t("Invalid hex value. Must be 3 or 6 hex digits or 'transparent'."));
    }
  }, [editingValue, onChange, popover, t]);

  const handlePickerChange = useCallback(
    (color: string) => {
      setEditingValue(color.replace(/^#/, ""));
      onInput?.(color);
    },
    [onInput],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value);
    setError(null); // clear error as soon as user types again
  }, []);

  const handleBrandClick = useCallback(
    (color: BrandKitColor) => {
      setEditingValue(color.hex.replace(/^#/, ""));
      onInput?.(color.hex);
    },
    [onInput],
  );

  const pickerColor = colour === "transparent" ? "#000000" : colour;

  return (
    <Popover
      anchorEl={popover.anchorRef.current}
      open={popover.open}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      sx={{ width: "600px" }}
    >
      <Box display="grid" gridTemplateColumns={"1fr 1fr"} gap={1} m={1}>
        <Box>
          <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
        </Box>
        <Stack gap={3}>
          <Box display={"flex"} justifyContent={"center"} gap={1}>
            <EyeDropper onChange={handlePickerChange} />
            <OutlinedInput
              inputProps={{ "aria-label": "hex" }}
              value={editingValue}
              startAdornment={editingValue.match(TRANSPARENT_RE) ? null : "#"}
              onChange={handleInputChange}
              error={!!error}
              sx={{
                minWidth: "6em",
                ".MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "inherit !important",
                  borderWidth: "1px",
                },
              }}
              size="small"
              inputRef={inputRef}
              fullWidth
            />
          </Box>

          {error && <FormHelperText error>{error}</FormHelperText>}

          <BrandColours onClick={handleBrandClick} />

          <Box display={"flex"} gap={2} justifyContent={"space-between"}>
            <Button
              data-analytics-id="colour-picker-popover-cancel"
              color="error"
              variant="outlined"
              onClick={handleClose}
            >
              {t("Cancel")}
            </Button>
            <Button
              data-analytics-id="colour-picker-popover-ok"
              variant="outlined"
              onClick={handleConfirm}
            >
              {t("OK")}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Popover>
  );
};
